import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  DocumentReference,
  DocumentData,
  onSnapshot,
  Unsubscribe,
} from "firebase/firestore";
import { Client, ICreateClient } from "@freetech/models/projects";
import { useAuth } from "hooks/auth/useAuth";
import { useAdminPermissions } from "hooks/auth/useAdminPermissions";
import { useEffect, useMemo } from "react";
import {
  loadNestedClients,
  createClient,
  updateClient,
  deleteClient,
} from "core/admin/clients";
import { db } from "@freetech/models/db";

export const useAdminClients = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const { getAccessibleClientIds, checkPermission } = useAdminPermissions();

  const {
    data: allClients,
    isLoading,
    isError,
  }: UseQueryResult<Client[], Error> = useQuery<Client[], Error>({
    queryKey: ["clients"],
    queryFn: () => loadNestedClients(),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const clients = useMemo(() => {
    if (!allClients) return undefined;

    if (!checkPermission("clients", "view")) {
      return [];
    }

    const accessibleClientIds = getAccessibleClientIds();

    if (accessibleClientIds === null) {
      return allClients;
    }

    if (accessibleClientIds.length === 0) {
      return [];
    }

    return allClients.filter(
      (client) => client.id && accessibleClientIds.includes(client.id)
    );
  }, [allClients, getAccessibleClientIds, checkPermission]);

  const addClientMutation = useMutation<
    DocumentReference<DocumentData>,
    Error,
    ICreateClient
  >({
    mutationFn: (c: ICreateClient) => createClient(c, currentUser),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });

  const updateClientMutation = useMutation<
    void,
    Error,
    { id: string; client: Partial<Client> }
  >({
    mutationFn: ({ id, client }) => updateClient(id, client),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });

  const deleteClientMutation = useMutation<void, Error, string>({
    mutationFn: (clientId: string) => deleteClient(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });

  useEffect(() => {
    let unsubscribe: Unsubscribe;

    const setupClientsListener = async () => {
      const clientsRef = db.clients;

      unsubscribe = onSnapshot(clientsRef, async () => {

        queryClient.invalidateQueries({ queryKey: ["clients"] });
      });
    };

    setupClientsListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [queryClient]);

  return {
    clients,
    allClients,
    isLoading,
    isError,
    addClient: addClientMutation,
    updateClient: updateClientMutation,
    deleteClient: deleteClientMutation,
  };
};
